import type { AllowedCountry } from '@repo/shared-config/app/constants/zendesk';
import { ZENDESK_DEFAULTS, ZENDESK_KEYS } from '@repo/shared-config/app/constants/zendesk';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { isNil } from 'es-toolkit';
import { loadScript } from '#utils/load-script';

const ENV = getEnvironmentVariables();

const COUNTRY_TO_HELP_CENTER_MAP = {
  fr: 'fr',
  de: 'de',
  it: 'it',
  es: 'es',
  // Fallback for FAQ articles that are not localized
  pt: 'pt',
  // Austrian users are mapped to German help center since they share the same language
  at: 'de',
  be: 'be',
  nl: 'nl',
} as const;

const isMounted = () => !isNil(window.zE);
const shouldMountSandBox = ENV.deployTarget !== 'production';

const _setZendeskLocale = (locale: string) => {
  if (isMounted()) {
    window.zE?.('messenger:set', 'locale', locale);
  }
};

const _loadZendesk = async (orgCountry: AllowedCountry) => {
  let zendeskKey: string;

  if (shouldMountSandBox) {
    zendeskKey = ZENDESK_KEYS.__sandbox__;
  } else {
    zendeskKey =
      orgCountry in ZENDESK_KEYS
        ? ZENDESK_KEYS[orgCountry as keyof typeof ZENDESK_KEYS] // Cast is safe here as we checked the country is in the ZENDESK_KEYS object
        : ZENDESK_KEYS[ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY];
  }

  window.zESettings = {
    messenger: {
      canUserSeeConversationList: false,
    },
  };

  try {
    await loadScript(`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`, {
      scriptId: 'ze-snippet',
    });
  } catch {
    // we ignore the error, as Zendesk is not critical for our app to work, and we don't want to flag the error on Sentry
  }
};

const _openZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'open');
  }
};

// We delay opening the widget to give time for the redirections to finish and the page to load a bit
const _delayedOpen = () => {
  setTimeout(() => {
    _openZendesk();
    window.removeEventListener('ze-loaded', _delayedOpen);
  }, 1500);
};

export const openZendeskAfterMounting = () => {
  if (isMounted()) {
    _delayedOpen();
  } else {
    window.addEventListener('ze-loaded', _delayedOpen);
  }
};

export const closeZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'close');
  }
};

export const loadAndAuthenticateZendesk = async ({
  orgCountry,
  userLocale,
}: {
  orgCountry: string;
  userLocale: string;
}) => {
  try {
    const orgCountryToUse =
      orgCountry.toLowerCase() in COUNTRY_TO_HELP_CENTER_MAP
        ? COUNTRY_TO_HELP_CENTER_MAP[
            orgCountry.toLowerCase() as keyof typeof COUNTRY_TO_HELP_CENTER_MAP
          ]
        : ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    await _loadZendesk(orgCountryToUse);
    _setZendeskLocale(userLocale);
    window.dispatchEvent(new Event('ze-loaded'));
  } catch (error) {
    // eslint-disable-next-line no-console -- Done via a console.error for now as there is no clear Sentry contract yet
    console.error('Error loading and authenticating the Zendesk widget:', error);
    // this.errorHandler('Error loading and authenticating the Zendesk widget:', error);
  }
};

export const unmountZendesk = () => {
  if (isMounted()) {
    window.zE?.('messenger', 'hide');
    window.zE?.('messenger', 'logoutUser');
  }

  // window.unreadMessagesCount = 0;

  window.zE = undefined;
  window.zEmbed = undefined;
  window.zEACLoaded = undefined;
  window.zESettings = undefined;

  document.getElementById('ze-snippet')?.remove();

  const launcher = document.querySelector('iframe[id="launcher"]');
  const widgetWindow = document.querySelector('iframe[name="Messaging window"]');
  const frame = document.querySelector('iframe[data-product="web_widget"]');

  if (launcher?.parentNode) {
    launcher.parentNode.removeChild(launcher);
  }
  if (widgetWindow?.parentNode) {
    widgetWindow.parentNode.removeChild(widgetWindow);
  }
  if (frame?.parentNode) {
    frame.parentNode.removeChild(frame);
  }
};

declare global {
  interface Window {
    zE: ((command: string, ...args: unknown[]) => void) | undefined;
    zEmbed: ((command: string, ...args: unknown[]) => void) | undefined;
    zEACLoaded: ((command: string, ...args: unknown[]) => void) | undefined;
    zESettings: Record<string, unknown> | undefined;
  }
}
